/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { UniversalTrackerPlain } from './universalTracker';
import type { IntegrationProvider } from './app-integration';
import type { PartialAssuranceField } from '@g17eco/types/assurance';
import { SurveyRoles, SurveyType } from './surveyCommon';
import type { UnitConfig } from './survey';
import { DataPeriods } from '@utils/dataPeriods';
import { StakeholderGroup } from '../model/stakeholderGroup';
import { ExistingEvidenceFile } from './file';
import { LatestUtrvNotes, UtrvNote } from './utrvNote';
import { SurveyUserRoles } from '@constants/user';
import { UserMin } from './user';
import { SurveyWorkgroup, Workgroup } from './workgroup';
import { ValueAggregation, UtrValueType } from '@g17eco/core';
import { UtrvPermission } from './permission';

export enum ValueDataSourceType {
  AIAutoAnswer = 'ai_auto_answer',
  ExternalConnection = 'external-connection', // integration
  SecondaryConnection = 'secondary-connection',
  DocumentConnection = 'document-connection',
  DataTransfer = 'data_transfer',
}

export interface IpLocation {
  ip: string;
  latitude?: number;
  longitude?: number;
}

export enum UtrvAssuranceStatus {
  /** Question is added to assurance portfolio, but not yet completed **/
  Created = 'created',
  /** Question is completed and should no longer be updated **/
  Completed = 'completed',
  /** Question has been marked to be explicitly open for modifications **/
  CompletedOpen = 'completed_open',
  Rejected = 'rejected',
  /** Question has been updated after completion **/
  Restated = 'restated',
  /** Question has been marked to be partial assurance and can assure later **/
  Partial = 'partial',
}

type ValueType = number | string | undefined;
export type ValueDataObject = Record<string, ValueType>
export type ValueDataData = string | string[] | ValueDataObject

export interface EvidenceData {
  documentId: string;
  description?: string;
}

export interface ValueHistory extends Partial<UtrvNote>{
  _id: string;
  action: string;
  userId: string;
  value: number | undefined;
  /** @deprecated */
  sampleSize?: number;
  date: string;
  /** @deprecated Use evidenceData instead */
  evidence?: string[];
  evidenceData?: EvidenceData[];
  unit?: string;
  numberScale?: string;
  assuranceStatus?: UtrvAssuranceStatus;
  assuranceFields?: PartialAssuranceField[];
  valueType?: UtrValueType;
  valueData?: ValueData;
  location?: IpLocation;
}


export interface UtrvHistory {
  latestHistory: {
    documents: ExistingEvidenceFile[],
    stakeholderHistory?: ValueHistory,
    verifierHistory?: ValueHistory
  }
}

export interface UtrvHistoryInfoInterface extends UniversalTrackerValuePlain {
  documents: ExistingEvidenceFile[];
  users: UserMin[];
  universalTracker: UniversalTrackerPlain[];
  history: ValueHistory[];
  surveyType?: SurveyType;
  providers?: Array<Pick<IntegrationProvider, 'code' | 'name' | 'logo' | 'icon'>>;
}

export type KeyUtrvs = 'value' |
  'valueData' |
  'effectiveDate' |
  'assuranceStatus' |
  'universalTrackerId' |
  'period' |
  'type';

// Aggregated Utrv
export type UtrValueFields = Pick<UniversalTrackerValuePlain, KeyUtrvs>
export type UtrvDataState = UniversalTrackerValuePlain | UtrValueFields;

export interface UtrvComment {
  _id: string;
  userId: string;
  user: {
    _id: string;
    name: string;
  };
  created: string;
  text: string;
}

export interface UtrvCommentsResponse {
  _id: string;
  utrvId: string;
  created: string;
  items: UtrvComment[];
  users: Pick<UserMin, '_id' | 'firstName' | 'surname'>[];
}

export interface ConversionData {
  unit?: string;
  defaultUnit?: string;
  numberScale?: string;
  defaultNumberScale?: string;
  value?: string | number;
  utr: Pick<UniversalTrackerPlain, '_id' | 'unit' | 'unitInput' | 'unitType' | 'numberScale' | 'numberScaleInput' | 'valueType' | 'valueValidation'>;
  isCurrency: boolean;
  suffix?: string;
  prefix?: string;
}

export interface UniversalTrackerValueExtended extends UniversalTrackerValuePlain {
  universalTracker: UniversalTrackerPlain
}

export interface UtrvIds {
  [SurveyUserRoles.Stakeholder]: string[];
  [SurveyUserRoles.Verifier]: string[];
}

export interface AIAutoAnswerSource {
  type: ValueDataSourceType.AIAutoAnswer;
  data?: {
    model: string;
    backgroundJobId: string;
    valueListCode?: string;
  };
}

interface ConnectionSourceData {
  calculationGroupCode: string;
  connectionId: string;
  value: number | string;
  unit?: string;
  numberScale?: string;
  valueListCode?: string;
}

interface ExternalConnectionSourceData extends ConnectionSourceData {
  integrationCode: string;
}

export interface ExternalConnectionSource {
  type: ValueDataSourceType.ExternalConnection;
  data: ExternalConnectionSourceData;
}

export interface SecondaryConnectionSource {
  type: ValueDataSourceType.SecondaryConnection;
  data: ConnectionSourceData;
}

export interface DocumentConnectionSource {
  type: ValueDataSourceType.DocumentConnection;
  data: {
    documentId: string;
    relevantUtrId: string;
  } & Pick<ConnectionSourceData, 'valueListCode' | 'value' | 'unit' | 'numberScale'>;
}

export interface DataTransferSource {
  type: ValueDataSourceType.DataTransfer;
  data: {
    integrationCode: string;
    connectionId: string;
    dataTransferId: string;
    snapshotAt?: string;
    value: number | string | string[];
    unit?: string;
    numberScale?: string;
    valueListCode?: string;
  };
}

export type ValueDataSourceConnection = ExternalConnectionSource | SecondaryConnectionSource | DocumentConnectionSource | DataTransferSource;

export type ValueDataSource = AIAutoAnswerSource | ValueDataSourceConnection;

interface ValueDataTableColumn {
  code: string;
  value?: string | number | string[];
  unit?: string;
  numberScale?: string;
  source?: ValueDataSource;
}

interface ValueDataInput {
  data?: any;
  value?: number;
  unit?: string;
  numberScale?: string;
  table?: ValueDataTableColumn[][];
  source?: ValueDataSource | ValueDataSource[];
}

export interface ValueData {
  data?: any;
  isImported?: boolean;
  table?: ValueDataTableColumn[][];
  explain?: string;
  notApplicableType?: string;
  input?: ValueDataInput;
  source?: ValueDataSource | ValueDataSource[];
}

export type SubmitValueData = ValueData & {
  input: ValueDataInput;
}

interface SecondaryComposite {
  surveyId?: string;
  blueprint?: string;
  configCode: string;
}

export interface CompositeData extends SecondaryComposite {
  secondary?: SecondaryComposite[];
}

export interface LedgerUniversalTrackerValue {
  created: string;
  lastUpdatedDate: string;
  documentId: string;
  utrvId: string;
  historyIds: string[];
}

export type DisaggregationSummary = Record<string, any>;

export interface SourceItem {
  utrvId: string;
  /** points to the last action=updated history, where data was taken from **/
  historyId: string;

  /** points to the last history when the source item was used **/
  latestHistoryId: string | undefined;
  /** for convenience, we pull the status of the utrv at the time of use **/
  latestStatus: string | undefined;
}

export type UtrvInitiativeRef = {
  _id: string;
  name: string;
};

export type UniversalTrackerModalServiceUtrv = Pick<UniversalTrackerValuePlain,
  '_id' |
  'status' |
  'universalTrackerId' |
  'initiativeId' |
  'type' |
  'effectiveDate' |
  'value' |
  'valueData' |
  'assuranceStatus' |
  'compositeData' |
  'lastUpdated' |
  'valueType' |
  'stakeholders' |
  'aggregationCount' |
  'ledgerUniversalTrackerValue' |
  'period' |
  'disaggregation'
> & {
  surveyType?: SurveyType;
  initiative?: UtrvInitiativeRef;
  surveyRoles?: SurveyRoles;
  surveyStakeholders?: StakeholderGroup;
  surveyUnitConfig?: UnitConfig;
  surveyWorkgroups?: SurveyWorkgroup[];
};

export type DisaggregationUtrv =
  | Pick<
      UniversalTrackerValuePlain,
      'effectiveDate' | 'type' | 'universalTrackerId' | 'initiativeId' | 'value' | 'valueData' | 'compositeData' | '_id'
    >
  | UniversalTrackerValuePlain;

export type UtrvWorkgroup = Workgroup & { utrvIds: string[]; permission: UtrvPermission };

export interface UniversalTrackerValuePlain<T = string> {
  _id: string;
  universalTrackerId: T;
  initiativeId: T;
  sourceType: string;
  sourceCode?: string;
  effectiveDate: string;
  type: string;
  status: string;
  valueData?: ValueData;
  valueType?: string;
  history?: ValueHistory[];
  stakeholders: {
    stakeholder: string[];
    verifier: string[];
  };
  unit?: string;
  sampleSize?: number;
  value?: number;
  numberScale?: string;
  compositeData?: CompositeData;
  sourceItems?: SourceItem[];
  ledgerUniversalTrackerValue?: LedgerUniversalTrackerValue;
  disaggregationSummary?: DisaggregationSummary;
  period?: DataPeriods;
  assuranceStatus?: UtrvAssuranceStatus;
  aggregationCount?: number;
  disaggregation?: DisaggregationUtrv[];
  evidenceRequired?: boolean;
  noteRequired?: boolean;
  verificationRequired?: boolean;
  isPrivate?: boolean;
  lastUpdated: string;
  note?: string;
  notes?: LatestUtrvNotes;
  created?: string;
  valueAggregation?: ValueAggregation;
  permissions?: UtrvPermission[];
  workgroups?: UtrvWorkgroup[];
}
